import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../auth/auth-context';
import { supabase } from '../auth/supabase-client';

export const albaDataChangedEvent = 'alba:data-changed';

type DataChangeDetail = { table: string; eventType: string };

const liveTables = [
  'actions',
  'action_events',
  'action_proofs',
  'calendar_events',
  'captures',
  'capture_drafts',
  'decisions',
  'dossiers',
  'dossier_facts',
  'direction_information_items',
  'documents',
  'jobs',
  'meeting_agenda_items',
  'meeting_agenda_versions',
  'meeting_ai_extraction_proposals',
  'meeting_preparations',
  'meeting_report_versions',
  'meeting_transcript_versions',
  'meetings',
  'notifications',
  'proposals',
  'proposal_arbitration_history',
  'subjects',
] as const;

export function notifyDataChanged(table: string, eventType = 'LOCAL') {
  window.dispatchEvent(
    new CustomEvent<DataChangeDetail>(albaDataChangedEvent, { detail: { table, eventType } }),
  );
}

export function useLiveRefresh(tables: readonly string[], refresh: () => void | Promise<void>) {
  const refreshRef = useRef(refresh);
  const tableKey = tables.join('|');
  const acceptedTables = useMemo(() => new Set(tableKey.split('|').filter(Boolean)), [tableKey]);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    let timer: number | undefined;
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<DataChangeDetail>).detail;
      if (!detail || !acceptedTables.has(detail.table)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void refreshRef.current(), 180);
    };
    window.addEventListener(albaDataChangedEvent, listener);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(albaDataChangedEvent, listener);
    };
  }, [acceptedTables]);
}

export function LiveDataBridge() {
  const { member } = useAuth();
  const [degraded, setDegraded] = useState(false);

  useEffect(() => {
    if (!supabase || !member) return;
    const client = supabase;
    setDegraded(false);
    let channel = client.channel(`alba-direction-${member.organization_id}-${member.id}`);
    for (const table of liveTables) {
      channel = channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table,
          filter: `organization_id=eq.${member.organization_id}`,
        },
        (payload) => notifyDataChanged(table, payload.eventType),
      );
    }
    channel.subscribe((status) => {
      const state = String(status);
      if (state === 'SUBSCRIBED') setDegraded(false);
      if (state === 'CHANNEL_ERROR' || state === 'TIMED_OUT') setDegraded(true);
    });
    return () => {
      void client.removeChannel(channel);
    };
  }, [member]);

  if (!degraded) return null;
  return (
    <p className="realtime-status" role="status">
      Mise à jour instantanée momentanément indisponible. Les données sont resynchronisées à chaque
      ouverture d’écran.
    </p>
  );
}
